import { adminAuthHeaders } from '../contexts/AdminAuthContext';
import { fetchWithTimeout } from './api';

// Configuración de la API Admin
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const isDev = process.env.NODE_ENV === 'development';
function devLog(...args: unknown[]) {
  if (isDev) console.log(...args);
}

devLog('🔧 [AdminAPI] API_BASE_URL configurada:', API_BASE_URL);

function parseApiErrorDetail(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;
  const detail = (err as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg?: string }).msg || '');
        }
        return '';
      })
      .filter(Boolean);
    if (parts.length) return parts.join(', ');
  }
  return fallback;
}

function withAuth(init?: RequestInit): RequestInit {
  return { ...init, headers: adminAuthHeaders(init?.headers) };
}

// Tipos de datos para Admin
export interface DashboardStats {
  total_folders: number;
  total_photos: number;
  total_revenue: number;
  total_transactions: number;
  recent_transactions: number;
  revenue_change_percentage: number;
}

export interface Activity {
  type: string;
  description: string;
  date: string;
  status: string;
  amount?: number;
  amount_is_net?: boolean;
  currency?: string;
}

export interface Folder {
  name: string;
  photo_count: number;
  created_at: string;
  cover_image?: string;
  days?: string[];
  custom_date?: string;
  custom_text?: string;
  /** true si solo existe en metadata (ej. OTRAS ESCUELAS), no en disco */
  isVirtual?: boolean;
}

export interface FolderDisplayMetadata {
  date?: string;
  text?: string;
  /** Metadata por día (fecha/texto de visualización por cada día) */
  days?: Record<string, { date?: string; text?: string }>;
}

export interface DayFolder {
  date: string;
  photo_count: number;
}

/** Línea de carrito / pedido (foto o pack con varios archivos). */
export interface PaymentItem {
  id: string;
  filename: string;
  school: string;
  date: string;
  price: number;
  thumbnail?: string | null;
  files?: string[] | null;
}

export interface Payment {
  id: string;
  amount: number;
  status: string;
  customer_name: string;
  customer_email: string;
  created_at: string;
  items_count?: number;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  currency?: string;
  items?: PaymentItem[];
}

export interface PaymentSummary {
  total_amount: number;
  total_transactions: number;
  average_transaction: number;
  by_status: Record<string, { count: number; amount: number }>;
  by_month: Record<string, { count: number; amount: number }>;
}

export interface PaymentsListParams {
  page?: number;
  page_size?: number;
  q?: string;
  status?: string;
  school?: string;
  date_from?: string;
  date_to?: string;
  amount_min?: number;
  amount_max?: number;
  sort?: 'created_at_desc' | 'created_at_asc';
  sync_recent?: boolean;
  payment_id?: string;
}

export interface PaymentsListResponse {
  payments: Payment[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  schools: string[];
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  auth_provider: string;
  email_verified_at: string | null;
  created_at: string | null;
  purchases_count: number;
  searches_count: number;
}

export interface UsersListParams {
  page?: number;
  page_size?: number;
  q?: string;
  verified?: 'true' | 'false' | 'all';
  provider?: string;
}

export interface UsersListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AdminUserPurchase {
  id: string;
  payment_id: string;
  stripe_session_id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string | null;
  school: string | null;
  customer_email: string;
  customer_name: string;
}

export interface AdminUserSearch {
  id: string;
  school: string;
  session_date: string;
  match_count: number;
  created_at: string | null;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    display_name: string;
    auth_provider: string;
    email_verified_at: string | null;
    created_at: string | null;
    updated_at: string | null;
  };
  purchases: AdminUserPurchase[];
  searches: AdminUserSearch[];
  stats: {
    purchases_count: number;
    searches_count: number;
  };
}

export interface PhotoReport {
  folder_name: string;
  photo_count: number;
  total_size_mb: number;
  created_at: string;
}

export interface RevenueReport {
  period: string;
  amount: number;
  transactions: number;
  items_sold: number;
}

export interface SitePricing {
  individual_eur: number;
  pack_first_eur: number;
  pack_additional_eur: number;
  currency: string;
}

export interface Settings {
  stripe_publishable_key: string;
  tax_rate: number;
  frontend_url: string;
  backend_url: string;
  maintenance_enabled: boolean;
  pricing: SitePricing;
  platform_fee_percent?: number;
}

export interface PhotographerStripeStatus {
  connected?: boolean;
  stripe_account_id?: string | null;
  charges_enabled?: boolean;
  details_submitted?: boolean;
  payouts_enabled?: boolean;
  ready_for_payments?: boolean;
  platform_account?: boolean;
  message?: string;
}

// Servicio de API Admin
export const adminApiService = {
  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      devLog('🔍 Intentando conectar a:', `${API_BASE_URL}/admin/dashboard/stats`);
      const response = await fetch(`${API_BASE_URL}/admin/dashboard/stats`, withAuth());
      
      devLog('📡 Response status:', response.status);
      devLog('📡 Response headers:', response.headers);
      
      if (!response.ok) {
        const text = await response.text();
        console.error('❌ Response no OK:', text);
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Respuesta no es JSON:', text.substring(0, 200));
        throw new Error(`El servidor respondió con HTML en lugar de JSON. Verifica que REACT_APP_API_URL esté configurada correctamente. URL actual: ${API_BASE_URL}`);
      }
      
      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Error en getDashboardStats:', error);
      console.error('🔧 API_BASE_URL configurada:', API_BASE_URL);
      throw error;
    }
  },

  async getRecentActivity(limit: number = 10): Promise<Activity[]> {
    try {
      devLog('🔍 Intentando conectar a:', `${API_BASE_URL}/admin/dashboard/activity?limit=${limit}`);
      const response = await fetch(`${API_BASE_URL}/admin/dashboard/activity?limit=${limit}`, withAuth());
      
      if (!response.ok) {
        const text = await response.text();
        console.error('❌ Response no OK:', text);
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Respuesta no es JSON:', text.substring(0, 200));
        throw new Error(`El servidor respondió con HTML en lugar de JSON. Verifica la URL del backend: ${API_BASE_URL}`);
      }
      
      const data = await response.json();
      return data.activities;
    } catch (error) {
      console.error('Error en getRecentActivity:', error);
      console.error('🔧 API_BASE_URL configurada:', API_BASE_URL);
      throw error;
    }
  },

  // Folders
  async getFolders(): Promise<Folder[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders`, withAuth({
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }));
      if (!response.ok) throw new Error('Error obteniendo carpetas');
      const data = await response.json();
      devLog('🔍 [AdminAPI] Respuesta completa del backend:', data);
      devLog('🔍 [AdminAPI] data.folders:', data.folders);
      
      // Log detallado de cada carpeta
      data.folders.forEach((folder: any) => {
        devLog(`📁 Carpeta "${folder.name}":`, JSON.stringify(folder, null, 2));
      });
      
      return data.folders;
    } catch (error) {
      console.error('Error en getFolders:', error);
      throw error;
    }
  },

  async getFolderDisplayMetadata(): Promise<Record<string, FolderDisplayMetadata>> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/display-metadata`, withAuth());
      if (!response.ok) throw new Error('Error obteniendo metadata de carpetas');
      const data = await response.json();
      return data.metadata || {};
    } catch (error) {
      console.error('Error en getFolderDisplayMetadata:', error);
      throw error;
    }
  },

  async setFolderDisplayMetadata(folderName: string, meta: { date?: string; text?: string }): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/display-metadata`, withAuth({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_name: folderName, ...meta }),
      }));
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error guardando fecha/texto');
      }
    } catch (error) {
      console.error('Error en setFolderDisplayMetadata:', error);
      throw error;
    }
  },

  /** Fecha y/o texto de visualización para un día concreto dentro de una carpeta. */
  async setFolderDayDisplayMetadata(
    folderName: string,
    dayDate: string,
    meta: { date?: string; text?: string }
  ): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/display-metadata`, withAuth({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_name: folderName, day_date: dayDate, ...meta }),
      }));
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error guardando fecha/texto del día');
      }
    } catch (error) {
      console.error('Error en setFolderDayDisplayMetadata:', error);
      throw error;
    }
  },

  async removeFolderDisplayMetadata(folderName: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/display-metadata?folder_name=${encodeURIComponent(folderName)}`, withAuth({
        method: 'DELETE',
      }));
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error quitando carpeta de la lista');
      }
    } catch (error) {
      console.error('Error en removeFolderDisplayMetadata:', error);
      throw error;
    }
  },

  /** Orden de carpetas en la página de escuelas */
  async setFolderOrder(folderOrder: string[]): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/folder-order`, withAuth({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_order: folderOrder }),
      }));
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error guardando orden');
      }
    } catch (error) {
      console.error('Error en setFolderOrder:', error);
      throw error;
    }
  },

  async createFolder(folderName: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/create?folder_name=${encodeURIComponent(folderName)}`, withAuth({
        method: 'POST',
      }));
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error creando carpeta');
      }
    } catch (error) {
      console.error('Error en createFolder:', error);
      throw error;
    }
  },

  async renameFolder(oldName: string, newName: string): Promise<{ new_name: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/rename`, withAuth({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_name: oldName, new_name: newName }),
      }));
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error renombrando carpeta');
      }
      const data = await response.json();
      return { new_name: data.new_name || newName };
    } catch (error) {
      console.error('Error en renameFolder:', error);
      throw error;
    }
  },

  async deleteFolder(folderName: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/delete?folder_name=${encodeURIComponent(folderName)}`, withAuth({
        method: 'DELETE',
      }));
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error eliminando carpeta');
      }
    } catch (error) {
      console.error('Error en deleteFolder:', error);
      throw error;
    }
  },

  async setFolderCover(folderName: string, coverImage: File): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('cover_image', coverImage);

      const response = await fetch(`${API_BASE_URL}/folders/set-cover?folder_name=${encodeURIComponent(folderName)}`, withAuth({
        method: 'POST',
        body: formData,
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error estableciendo portada');
      }
    } catch (error) {
      console.error('Error en setFolderCover:', error);
      throw error;
    }
  },

  async setDayCover(folderName: string, dayDate: string, coverImage: File): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('cover_image', coverImage);

      const response = await fetch(`${API_BASE_URL}/folders/set-day-cover?folder_name=${encodeURIComponent(folderName)}&day_date=${dayDate}`, withAuth({
        method: 'POST',
        body: formData,
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error estableciendo portada del día');
      }
    } catch (error) {
      console.error('Error en setDayCover:', error);
      throw error;
    }
  },

  async createDayFolder(folderName: string, dayDate: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/create-day?folder_name=${encodeURIComponent(folderName)}&day_date=${dayDate}`, withAuth({
        method: 'POST',
      }));
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error creando día');
      }
    } catch (error) {
      console.error('Error en createDayFolder:', error);
      throw error;
    }
  },

  async getFolderDays(folderName: string): Promise<DayFolder[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/${encodeURIComponent(folderName)}/days`, withAuth());
      if (!response.ok) throw new Error('Error obteniendo días');
      const data = await response.json();
      return data.days || [];
    } catch (error) {
      console.error('Error en getFolderDays:', error);
      throw error;
    }
  },

  async deleteDayFolder(folderName: string, dayDate: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/delete-day?folder_name=${encodeURIComponent(folderName)}&day_date=${encodeURIComponent(dayDate)}`, withAuth({ method: 'DELETE' }));
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error eliminando día');
      }
    } catch (error) {
      console.error('Error en deleteDayFolder:', error);
      throw error;
    }
  },

  /**
   * Encola embeddings + clusters (misma cola que POST /indexing/start).
   * `force: true` fuerza reprocesar todo el día o la raíz de la carpeta.
   */
  async queueEmbeddingsProcess(
    folderName: string,
    options?: { day?: string; force?: boolean },
  ): Promise<{
    status: string;
    message: string;
    folder_name: string;
    day?: string | null;
    force?: boolean;
  }> {
    const qs = new URLSearchParams();
    qs.set('folder_name', folderName);
    if (options?.day) qs.set('day', options.day);
    if (options?.force) qs.set('force', 'true');
    const response = await fetch(`${API_BASE_URL}/admin/embeddings/process?${qs.toString()}`, withAuth({ method: 'POST' },));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error encolando índice facial');
    }
    return response.json();
  },

  // Photos
  async uploadPhotos(
    folderName: string,
    files: File[],
    onProgress?: (progress: {
      uploaded: number;
      total: number;
      percentage: number;
      currentFile?: string;
      skipped?: number;
      attempt?: number;
    }) => void
  ): Promise<any> {
    try {
      const totalSelected = files.length;
      const MAX_RETRIES = 2;
      // Por intento: suficiente para fotos grandes en conexiones lentas.
      const REQUEST_TIMEOUT_MS = 90000; // 90s
      // Presupuesto máximo absoluto por archivo (todos los intentos incluidos).
      // No es un límite de tamaño: es solo un seguro para que un archivo con
      // problemas de red nunca bloquee el resto de la subida indefinidamente.
      const HARD_FILE_BUDGET_MS = 5 * 60 * 1000; // 5 min
      const PAUSE_BETWEEN_MS = 250;
      const allResults: Array<{ filename: string }> = [];
      const allErrors: string[] = [];

      let folder = folderName;
      let day: string | null = null;
      if (folderName.includes('/')) {
        const parts = folderName.split('/').filter(Boolean);
        if (parts.length >= 2) {
          folder = parts[0];
          day = parts[1];
        }
      }

      const qsBase = new URLSearchParams();
      qsBase.set('folder_name', folder);
      if (day) qsBase.set('day', day);
      qsBase.set('index', 'false');

      // Omitir fotos que ya están en el servidor (reintentos tras quedarse a medias).
      // Se compara el tamaño: una foto que quedó truncada o a 0 bytes debe volver
      // a subirse, no darse por buena solo porque el nombre existe.
      let toUpload = files;
      let skipped = 0;
      try {
        const existing = await this.getFolderPhotos(folderName);
        const sizeOnServer = new Map<string, number>(
          existing.map((p: { filename: string; size?: number }) => [p.filename, Number(p.size) || 0])
        );
        toUpload = files.filter((f) => sizeOnServer.get(f.name) !== f.size);
        skipped = totalSelected - toUpload.length;
        if (skipped > 0) {
          devLog(`⏭️ ${skipped} fotos ya estaban completas en el servidor, se omiten`);
        }
      } catch {
        // Si falla el listado, subimos todo lo seleccionado.
      }

      if (toUpload.length === 0) {
        return {
          status: 'success',
          uploaded: 0,
          skipped,
          photos: [],
          errors: allErrors,
          message:
            skipped > 0
              ? `Las ${skipped} fotos seleccionadas ya estaban en el servidor`
              : 'No hay fotos válidas para subir',
        };
      }

      devLog(`📤 Subiendo ${toUpload.length} fotos (una a una, con presupuesto máx. ${HARD_FILE_BUDGET_MS / 1000}s por archivo)`);

      const attemptOnce = async (file: File): Promise<void> => {
        const formData = new FormData();
        formData.append('photos', file);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
          const res = await fetch(`${API_BASE_URL}/photos/upload?${qsBase.toString()}`, withAuth({
            method: 'POST',
            body: formData,
            signal: controller.signal,
          }));
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || res.statusText || 'Error subiendo foto');
          }
          const data = await res.json();
          const uploaded = data.photos || [];
          allResults.push(...uploaded);
          if (data.errors?.length) allErrors.push(...data.errors);
        } finally {
          clearTimeout(timeoutId);
        }
      };

      const uploadWithRetries = async (file: File): Promise<void> => {
        let lastError: unknown;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            await attemptOnce(file);
            return;
          } catch (e) {
            lastError = e;
            console.warn(`⚠️ ${file.name}: intento ${attempt + 1}/${MAX_RETRIES + 1} falló:`, e);
            if (attempt < MAX_RETRIES) {
              await new Promise((r) => setTimeout(r, 900 * (attempt + 1)));
            }
          }
        }
        throw lastError instanceof Error ? lastError : new Error(String(lastError));
      };

      /** Nunca deja que un archivo bloquee el resto: si se pasa del presupuesto, se marca como error y se sigue. */
      const uploadWithHardBudget = (file: File): Promise<'ok' | 'error' | 'timeout'> =>
        new Promise((resolve) => {
          let settled = false;
          const budgetTimer = setTimeout(() => {
            if (settled) return;
            settled = true;
            console.error(`⏱️ ${file.name}: superó el presupuesto de ${HARD_FILE_BUDGET_MS / 1000}s, se omite y se continúa`);
            resolve('timeout');
          }, HARD_FILE_BUDGET_MS);

          uploadWithRetries(file)
            .then(() => {
              if (settled) return;
              settled = true;
              clearTimeout(budgetTimer);
              resolve('ok');
            })
            .catch((e) => {
              if (settled) return;
              settled = true;
              clearTimeout(budgetTimer);
              allErrors.push(`${file.name}: ${(e as Error).message}`);
              resolve('error');
            });
        });

      let done = 0;
      const startedAt = Date.now();
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        if (onProgress) {
          onProgress({
            uploaded: done,
            total: toUpload.length,
            percentage: Math.round((done / toUpload.length) * 100),
            currentFile: file.name,
            skipped,
          });
        }
        devLog(`  → [${i + 1}/${toUpload.length}] ${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
        const outcome = await uploadWithHardBudget(file);
        if (outcome === 'ok') done += 1;
        if (outcome === 'timeout') {
          allErrors.push(`${file.name}: tardó demasiado (>${HARD_FILE_BUDGET_MS / 1000}s), se omitió`);
        }
        if (onProgress) {
          onProgress({
            uploaded: done,
            total: toUpload.length,
            percentage: Math.round((done / toUpload.length) * 100),
            currentFile: file.name,
            skipped,
          });
        }
        if (i < toUpload.length - 1) {
          await new Promise((r) => setTimeout(r, PAUSE_BETWEEN_MS));
        }
      }
      devLog(`✅ Subida terminada en ${((Date.now() - startedAt) / 1000).toFixed(0)}s: ${done}/${toUpload.length} ok, ${allErrors.length} errores`);

      const uploadedNames = allResults.map((p) => p.filename).filter(Boolean);
      if (uploadedNames.length > 0) {
        try {
          const qsIndex = new URLSearchParams();
          qsIndex.set('folder_name', folder);
          if (day) qsIndex.set('day', day);
          await fetch(`${API_BASE_URL}/indexing/start?${qsIndex.toString()}`, withAuth({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ new_filenames: uploadedNames }),
          }));
          devLog('🧠 Indexado incremental encolado al finalizar la subida');
        } catch (e) {
          console.warn('⚠️ No se pudo encolar el indexado (no bloqueante):', e);
        }
      }

      return {
        status: 'success',
        uploaded: allResults.length,
        skipped,
        photos: allResults,
        errors: allErrors,
        message: `${allResults.length} de ${toUpload.length} fotos subidas${
          skipped ? ` (${skipped} ya existían)` : ''
        }`,
      };
    } catch (error) {
      console.error('Error en uploadPhotos:', error);
      throw error;
    }
  },

  async getFolderPhotos(folderName: string): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/photos/list?folder_name=${encodeURIComponent(folderName)}`, withAuth());
      if (!response.ok) throw new Error('Error obteniendo fotos');
      const data = await response.json();
      return data.photos || [];
    } catch (error) {
      console.error('Error en getFolderPhotos:', error);
      throw error;
    }
  },

  async deletePhoto(folderName: string, filename: string): Promise<void> {
    try {
      const params = new URLSearchParams();
      params.set('folder_name', folderName);
      params.set('filename', filename);
      const response = await fetch(`${API_BASE_URL}/photos/delete?${params.toString()}`, withAuth({
        method: 'DELETE',
      }));
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error eliminando foto');
      }
    } catch (error) {
      console.error('Error en deletePhoto:', error);
      throw error;
    }
  },

  // Payments
  async getPaymentsList(params: PaymentsListParams = {}): Promise<PaymentsListResponse> {
    try {
      const qs = new URLSearchParams();
      if (params.page != null) qs.set('page', String(params.page));
      if (params.page_size != null) qs.set('page_size', String(params.page_size));
      if (params.q) qs.set('q', params.q);
      if (params.status) qs.set('status', params.status);
      if (params.school) qs.set('school', params.school);
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
      if (params.amount_min != null) qs.set('amount_min', String(params.amount_min));
      if (params.amount_max != null) qs.set('amount_max', String(params.amount_max));
      if (params.sort) qs.set('sort', params.sort);
      if (params.payment_id) qs.set('payment_id', params.payment_id);
      if (params.sync_recent) qs.set('sync_recent', 'true');
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const response = await fetch(`${API_BASE_URL}/admin/payments${suffix}`, withAuth());
      if (!response.ok) throw new Error('Error obteniendo pagos');
      const data = await response.json();
      return {
        payments: data.payments || [],
        total: data.total ?? 0,
        page: data.page ?? 1,
        page_size: data.page_size ?? 25,
        total_pages: data.total_pages ?? 0,
        schools: data.schools || [],
      };
    } catch (error) {
      console.error('Error en getPaymentsList:', error);
      throw error;
    }
  },

  // Users
  async getUsers(params: UsersListParams = {}): Promise<UsersListResponse> {
    const qs = new URLSearchParams();
    if (params.page != null) qs.set('page', String(params.page));
    if (params.page_size != null) qs.set('page_size', String(params.page_size));
    if (params.q) qs.set('q', params.q);
    if (params.verified) qs.set('verified', params.verified);
    if (params.provider) qs.set('provider', params.provider);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/users${suffix}`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error obteniendo usuarios');
    }
    const data = await response.json();
    return {
      users: data.users || [],
      total: data.total ?? 0,
      page: data.page ?? 1,
      page_size: data.page_size ?? 25,
      total_pages: data.total_pages ?? 0,
    };
  },

  async getUser(userId: string): Promise<AdminUserDetail> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(userId)}`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error obteniendo usuario');
    }
    const data = await response.json();
    return {
      user: data.user,
      purchases: data.purchases || [],
      searches: data.searches || [],
      stats: data.stats || { purchases_count: 0, searches_count: 0 },
    };
  },

  async verifyUserEmail(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(userId)}/verify-email`, withAuth({ method: 'POST' },));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error verificando email');
    }
  },

  async deleteUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(userId)}`, withAuth({
      method: 'DELETE',
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error eliminando usuario');
    }
  },

  async getPayment(paymentId: string): Promise<Payment> {
    const response = await fetch(`${API_BASE_URL}/stripe/payment/${encodeURIComponent(paymentId)}`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error obteniendo el pedido');
    }
    const data = await response.json();
    return data.payment as Payment;
  },

  async lookupStripeSession(
    email: string,
    amount?: number,
  ): Promise<{
    stripe_session_id: string;
    stripe_session_ids: string[];
    missing_session_ids: string[];
    count: number;
    missing_count: number;
  }> {
    const params = new URLSearchParams({ email });
    if (amount != null) params.set('amount', String(amount));
    const response = await fetch(`${API_BASE_URL}/admin/payments/lookup-stripe?${params}`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Sesión no encontrada en Stripe');
    }
    const data = await response.json();
    return {
      stripe_session_id: data.stripe_session_id as string,
      stripe_session_ids: data.stripe_session_ids || [data.stripe_session_id],
      missing_session_ids: data.missing_session_ids || [],
      count: data.count ?? 1,
      missing_count: data.missing_count ?? 0,
    };
  },

  async reconcilePayment(params: {
    stripe_session_id?: string;
    email?: string;
    amount?: number;
  }): Promise<{
    payment_id?: string;
    stripe_session_id?: string;
    client_success_url?: string;
    files_count: number;
    items_count: number;
    pending_found: boolean;
    reconstructed: boolean;
    items_empty?: boolean;
    imported_count?: number;
    imported?: Array<{
      payment_id: string;
      stripe_session_id: string;
      client_success_url: string;
      files_count: number;
      items_count: number;
    }>;
    errors?: { stripe_session_id: string; error: string }[];
  }> {
    const response = await fetch(`${API_BASE_URL}/admin/payments/reconcile`, withAuth({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error al importar pago');
    }
    return response.json();
  },

  async syncStripePayments(days = 90): Promise<{
    imported_count: number;
    checked: number;
    skipped: number;
    errors: { stripe_session_id: string; error: string }[];
  }> {
    const response = await fetch(`${API_BASE_URL}/admin/payments/sync-stripe?days=${encodeURIComponent(String(days))}`, withAuth({ method: 'POST' },));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Error sincronizando con Stripe');
    }
    return response.json();
  },

  async getPaymentSummary(startDate?: string, endDate?: string): Promise<PaymentSummary> {
    try {
      let url = `${API_BASE_URL}/admin/payments/summary`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, withAuth());
      if (!response.ok) throw new Error('Error obteniendo resumen de pagos');
      const data = await response.json();
      return data.summary;
    } catch (error) {
      console.error('Error en getPaymentSummary:', error);
      throw error;
    }
  },

  // Reports
  async getPhotosByFolder(): Promise<PhotoReport[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/reports/photos-by-folder`, withAuth());
      if (!response.ok) throw new Error('Error obteniendo reporte de fotos');
      const data = await response.json();
      return data.report;
    } catch (error) {
      console.error('Error en getPhotosByFolder:', error);
      throw error;
    }
  },

  async getRevenueByPeriod(period: 'day' | 'week' | 'month' | 'year'): Promise<RevenueReport[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/reports/revenue-by-period?period=${period}`, withAuth());
      if (!response.ok) throw new Error('Error obteniendo reporte de ingresos');
      const data = await response.json();
      return data.report;
    } catch (error) {
      console.error('Error en getRevenueByPeriod:', error);
      throw error;
    }
  },

  // Settings
  async getSettings(): Promise<Settings> {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/admin/settings`,
        withAuth(),
        30000,
      );
      if (response.status === 401) {
        throw new Error('Sesión expirada. Cierra sesión y vuelve a entrar.');
      }
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(parseApiErrorDetail(err, `Error obteniendo configuración (${response.status})`));
      }
      const data = await response.json();
      const s = data.settings;
      if (!s || typeof s !== 'object') {
        throw new Error('Respuesta inválida del servidor');
      }
      const pricing = s.pricing ?? {
        individual_eur: 15,
        pack_first_eur: 35,
        pack_additional_eur: 20,
        currency: 'eur',
      };
      return {
        ...s,
        maintenance_enabled: Boolean(s.maintenance_enabled),
        pricing,
      };
    } catch (error) {
      console.error('Error en getSettings:', error);
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new Error('No se pudo conectar con el servidor (tiempo agotado).');
      }
      if (error instanceof TypeError) {
        throw new Error('No se pudo conectar con el servidor.');
      }
      throw error;
    }
  },

  async setMaintenanceEnabled(enabled: boolean): Promise<{ enabled: boolean }> {
    const response = await fetch(`${API_BASE_URL}/admin/maintenance`, withAuth({
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }));
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Error ${response.status}`);
    }
    const data = await response.json();
    const result = { enabled: Boolean(data.enabled) };
    try {
      const { writeMaintenanceCache } = await import('../siteMaintenance');
      writeMaintenanceCache(result.enabled);
    } catch {
      // no-op
    }
    return result;
  },

  async updatePricing(payload: Omit<SitePricing, 'currency'> & { currency?: string }): Promise<SitePricing> {
    const response = await fetch(`${API_BASE_URL}/admin/pricing`, withAuth({
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        individual_eur: payload.individual_eur,
        pack_first_eur: payload.pack_first_eur,
        pack_additional_eur: payload.pack_additional_eur,
        currency: payload.currency ?? 'eur',
      }),
    }));
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Error ${response.status}`);
    }
    const data = await response.json();
    return data.pricing as SitePricing;
  },

  async updatePlatformFee(percent: number): Promise<number> {
    const response = await fetch(`${API_BASE_URL}/admin/platform-fee`, withAuth({
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform_fee_percent: percent }),
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || `Error ${response.status}`);
    }
    const data = await response.json();
    return Number(data.platform_fee_percent);
  },

  async getWatermark(): Promise<{
    has_custom: boolean;
    updated_at: string | null;
    preview_url: string;
    exists: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/admin/watermark`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return {
      has_custom: Boolean(data.has_custom),
      updated_at: data.updated_at ?? null,
      preview_url: data.preview_url || '/admin/watermark/file',
      exists: Boolean(data.exists),
    };
  },

  async fetchWatermarkBlobUrl(): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/admin/watermark/file`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  async uploadWatermark(file: File): Promise<{
    has_custom: boolean;
    updated_at: string | null;
    exists: boolean;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/admin/watermark`, withAuth({
      method: 'POST',
      body: formData,
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return {
      has_custom: Boolean(data.has_custom),
      updated_at: data.updated_at ?? null,
      exists: Boolean(data.exists),
    };
  },

  async resetWatermark(): Promise<{
    has_custom: boolean;
    updated_at: string | null;
    exists: boolean;
    cleared: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/admin/watermark`, withAuth({
      method: 'DELETE',
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return {
      has_custom: Boolean(data.has_custom),
      updated_at: data.updated_at ?? null,
      exists: Boolean(data.exists),
      cleared: Boolean(data.cleared),
    };
  },

  async getMyStripeStatus(): Promise<{
    stripe: PhotographerStripeStatus;
    platform_fee_percent: number;
    photographer_status?: string;
    can_connect?: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/admin/photographer/stripe`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    return response.json();
  },

  async startStripeConnect(): Promise<{ url: string; stripe: PhotographerStripeStatus }> {
    const response = await fetch(
      `${API_BASE_URL}/admin/photographer/stripe/connect`,
      withAuth({ method: 'POST' })
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status} al conectar Stripe`));
    }
    const data = await response.json();
    if (!data?.url || typeof data.url !== 'string') {
      throw new Error('Stripe no devolvió una URL de onboarding. Revisa la configuración de Connect.');
    }
    return data;
  },

  async openStripeDashboard(): Promise<string> {
    const response = await fetch(
      `${API_BASE_URL}/admin/photographer/stripe/dashboard`,
      withAuth({ method: 'POST' })
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    if (!data?.url) {
      throw new Error('Stripe no devolvió URL del panel');
    }
    return data.url as string;
  },

  // Utility
  getPhotoUrl(folderName: string, filename: string): string {
    return `${API_BASE_URL}/photos/view?folder_name=${encodeURIComponent(folderName)}&filename=${encodeURIComponent(filename)}`;
  },

  /**
   * Vista previa; si la foto está en subcarpeta de día, pasar `day`.
   * `maxWidth` acelera miniaturas en listados (ej. 320).
   */
  getPhotoPreviewUrl(
    folderName: string,
    filename: string,
    watermark: boolean = true,
    day?: string | null,
    maxWidth?: number,
  ): string {
    const qs = new URLSearchParams();
    qs.set('folder_name', folderName);
    qs.set('filename', filename);
    qs.set('watermark', String(watermark));
    if (day) qs.set('day', day);
    if (maxWidth != null) qs.set('max_width', String(maxWidth));
    return `${API_BASE_URL}/photos/preview?${qs.toString()}`;
  },

  async getPhotographers(status?: string): Promise<{
    photographers: AdminPhotographer[];
    total: number;
  }> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await fetch(`${API_BASE_URL}/admin/photographers${q}`, withAuth());
    if (!response.ok) throw new Error('Error listando fotógrafos');
    return response.json();
  },

  async getPhotographer(userId: string): Promise<AdminPhotographerDetail> {
    const response = await fetch(
      `${API_BASE_URL}/admin/photographers/${encodeURIComponent(userId)}`,
      withAuth()
    );
    if (!response.ok) throw new Error('Error obteniendo fotógrafo');
    const data = await response.json();
    return data.photographer;
  },

  async setPhotographerStatus(
    userId: string,
    status: 'pending' | 'approved' | 'rejected'
  ): Promise<AdminPhotographerDetail> {
    const response = await fetch(
      `${API_BASE_URL}/admin/photographers/${encodeURIComponent(userId)}/status`,
      withAuth({
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const detail = (err as { detail?: string | { msg?: string }[] }).detail;
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => (typeof d === 'string' ? d : d.msg)).filter(Boolean).join(', ')
            : 'Error actualizando estado';
      throw new Error(message);
    }
    const data = await response.json();
    const photographer = data.photographer ?? data;
    if (!photographer?.id) {
      throw new Error('Respuesta inválida al actualizar estado');
    }
    return photographer;
  },

  async listPromos(): Promise<{ promos: AdminPromo[]; display_seconds: number }> {
    const response = await fetch(`${API_BASE_URL}/admin/promos`, withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return {
      promos: data.promos || [],
      display_seconds: Number(data.display_seconds) || 2,
    };
  },

  async updatePromoSettings(display_seconds: number): Promise<number> {
    const response = await fetch(`${API_BASE_URL}/admin/promos/settings`, withAuth({
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_seconds }),
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return Number(data.display_seconds) || display_seconds;
  },

  async createPromo(body: AdminPromoInput): Promise<AdminPromo> {
    const response = await fetch(`${API_BASE_URL}/admin/promos`, withAuth({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return data.promo;
  },

  async updatePromo(id: string, body: Partial<AdminPromoInput>): Promise<AdminPromo> {
    const response = await fetch(`${API_BASE_URL}/admin/promos/${encodeURIComponent(id)}`, withAuth({
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return data.promo;
  },

  async deletePromo(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/promos/${encodeURIComponent(id)}`, withAuth({
      method: 'DELETE',
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
  },

  async reorderPromos(order: string[]): Promise<AdminPromo[]> {
    const response = await fetch(`${API_BASE_URL}/admin/promos/reorder`, withAuth({
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order }),
    }));
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return data.promos || [];
  },

  async uploadPromoAsset(id: string, kind: PromoAssetKind, file: File): Promise<AdminPromo> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(
      `${API_BASE_URL}/admin/promos/${encodeURIComponent(id)}/asset/${kind}`,
      withAuth({ method: 'POST', body: formData }),
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return data.promo;
  },

  async deletePromoAsset(id: string, kind: PromoAssetKind): Promise<AdminPromo> {
    const response = await fetch(
      `${API_BASE_URL}/admin/promos/${encodeURIComponent(id)}/asset/${kind}`,
      withAuth({ method: 'DELETE' }),
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const data = await response.json();
    return data.promo;
  },

  promoAssetPreviewUrl(id: string, kind: PromoAssetKind): string {
    return `${API_BASE_URL}/admin/promos/${encodeURIComponent(id)}/asset/${kind}`;
  },

  async fetchPromoAssetBlobUrl(id: string, kind: PromoAssetKind): Promise<string> {
    const response = await fetch(this.promoAssetPreviewUrl(id, kind), withAuth());
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiErrorDetail(err, `Error ${response.status}`));
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
};

export type PromoAssetKind = 'ad_es' | 'ad_en' | 'voucher_es' | 'voucher_en';

export interface AdminPromo {
  id: string;
  slug: string;
  name: string;
  href: string;
  active: boolean;
  sort_order: number;
  show_in_search: boolean;
  include_in_email: boolean;
  email_desc_es: string;
  email_desc_en: string;
  voucher_pdf_filename: string;
  has_ad_es?: boolean;
  has_ad_en?: boolean;
  has_voucher_es?: boolean;
  has_voucher_en?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AdminPromoInput {
  slug: string;
  name: string;
  href: string;
  active?: boolean;
  sort_order?: number;
  show_in_search?: boolean;
  include_in_email?: boolean;
  email_desc_es?: string;
  email_desc_en?: string;
  voucher_pdf_filename?: string;
}

export interface AdminPhotographerProfile {
  display_name: string;
  bio?: string | null;
  avatar_url?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  approved_at?: string | null;
  created_at?: string | null;
  stripe?: PhotographerStripeStatus | null;
}

export interface AdminPhotographer {
  id: string;
  email: string;
  display_name: string;
  email_verified: boolean;
  created_at?: string | null;
  profile: AdminPhotographerProfile | null;
  folders_count: number;
  folders: string[];
}

export interface AdminPhotographerDetail extends AdminPhotographer {
  sales?: {
    total_transactions: number;
    total_revenue: number;
  };
}

export default adminApiService;